// src/pages/Docker.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Container, Play, Square, RotateCw, Trash2, Terminal, FileText,
  RefreshCw, Plus, Info, BarChart2, ChevronRight, ChevronDown,
  Folder, File, Upload, Download, Edit3, Save, X, AlertTriangle,
  Search, Copy, CheckCircle, Clock, Cpu, HardDrive, Network,
  Layers, Settings, Activity, Package, ArrowUp, ArrowDown
} from 'lucide-react';
import api from '../utils/api';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';

// ─── Status badge ──────────────────────────────────────────────────────────
function StatusBadge({ state }) {
  const s = (state || '').toLowerCase();
  const map = {
    running: { label: 'Rulează', color: 'var(--success)', bg: '#22c55e20' },
    exited: { label: 'Oprit', color: 'var(--danger)', bg: '#ef444420' },
    stopped: { label: 'Oprit', color: 'var(--danger)', bg: '#ef444420' },
    paused: { label: 'Pauză', color: 'var(--warning)', bg: '#f59e0b20' },
    restarting: { label: 'Repornire', color: 'var(--accent)', bg: '#4f8ef720' },
    created: { label: 'Creat', color: 'var(--text-secondary)', bg: 'var(--bg-hover)' },
  };
  const cfg = map[s] || { label: state || '?', color: 'var(--text-secondary)', bg: 'var(--bg-hover)' };
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 8px', borderRadius: 10, color: cfg.color, background: cfg.bg, display: 'inline-flex', alignItems: 'center', gap: 4 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: cfg.color, display: 'inline-block' }} />
      {cfg.label}
    </span>
  );
}

// ─── Confirm dialog ────────────────────────────────────────────────────────
function ConfirmDialog({ msg, onConfirm, onCancel }) {
  return (
    <div className="modal-overlay" onClick={onCancel}>
      <div className="modal" style={{ maxWidth: 380 }} onClick={e => e.stopPropagation()}>
        <div className="modal-header"><span className="modal-title"><AlertTriangle size={16} color="var(--danger)" /> Confirmare</span></div>
        <div className="modal-body"><p style={{ color: 'var(--text-secondary)', fontSize: 14 }}>{msg}</p></div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onCancel}>Anulare</button>
          <button className="btn" style={{ background: 'var(--danger)', color: '#fff' }} onClick={onConfirm}>Confirmare</button>
        </div>
      </div>
    </div>
  );
}

// ─── Create Container Modal ────────────────────────────────────────────────
function CreateContainerModal({ onClose, onCreated }) {
  const [f, setF] = useState({ name: '', image: '', ports: '', env: '', volumes: '', restart: 'no', cmd: '' });
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const create = async () => {
    if (!f.image) return toast.error('Image este obligatoriu');
    setLoading(true);
    try {
      const payload = {
        name: f.name || undefined,
        image: f.image,
        restart: f.restart,
        cmd: f.cmd || undefined,
        ports: f.ports ? f.ports.split('\n').map(s => s.trim()).filter(Boolean) : [],
        env: f.env ? f.env.split('\n').map(s => s.trim()).filter(Boolean) : [],
        volumes: f.volumes ? f.volumes.split('\n').map(s => s.trim()).filter(Boolean) : [],
      };
      await api.post('/docker/containers', payload);
      toast.success('Container creat');
      onCreated();
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare la creare'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <span className="modal-title"><Container size={16} /> Container Nou</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-grid form-grid-2">
            <div className="form-group">
              <label className="form-label">Image *</label>
              <input className="form-input" value={f.image} onChange={e => set('image', e.target.value)} placeholder="nginx:latest, ubuntu:22.04..." />
            </div>
            <div className="form-group">
              <label className="form-label">Nume container</label>
              <input className="form-input" value={f.name} onChange={e => set('name', e.target.value)} placeholder="my-container" />
            </div>
            <div className="form-group">
              <label className="form-label">Restart Policy</label>
              <select className="form-select" value={f.restart} onChange={e => set('restart', e.target.value)}>
                <option value="no">No</option>
                <option value="always">Always</option>
                <option value="unless-stopped">Unless Stopped</option>
                <option value="on-failure">On Failure</option>
              </select>
            </div>
            <div className="form-group">
              <label className="form-label">Comandă</label>
              <input className="form-input" value={f.cmd} onChange={e => set('cmd', e.target.value)} placeholder="bash, /entrypoint.sh..." />
            </div>
            <div className="form-group">
              <label className="form-label">Porturi (unul per linie)</label>
              <textarea className="form-textarea" style={{ minHeight: 80 }} value={f.ports} onChange={e => set('ports', e.target.value)} placeholder={"8080:80\n443:443"} />
            </div>
            <div className="form-group">
              <label className="form-label">Env vars (KEY=VALUE, per linie)</label>
              <textarea className="form-textarea" style={{ minHeight: 80 }} value={f.env} onChange={e => set('env', e.target.value)} placeholder={"NODE_ENV=production\nDB_HOST=localhost"} />
            </div>
            <div className="form-group" style={{ gridColumn: '1/-1' }}>
              <label className="form-label">Volume mounts (per linie)</label>
              <textarea className="form-textarea" style={{ minHeight: 60 }} value={f.volumes} onChange={e => set('volumes', e.target.value)} placeholder={"/host/path:/container/path"} />
            </div>
          </div>
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Anulare</button>
          <button className="btn btn-primary" onClick={create} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Plus size={14} /> Creare</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Logs Panel ────────────────────────────────────────────────────────────
function LogsPanel({ containerId, containerName }) {
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(false);
  const [tail, setTail] = useState(200);
  const [search, setSearch] = useState('');
  const logRef = useRef(null);
  const toast = useToast();

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/docker/containers/${containerId}/logs`, { params: { tail, timestamps: true } });
      setLogs(data.logs || '');
      setTimeout(() => { if (logRef.current) logRef.current.scrollTop = logRef.current.scrollHeight; }, 100);
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare logs'); }
    finally { setLoading(false); }
  }, [containerId, tail]);

  useEffect(() => { fetchLogs(); }, [fetchLogs]);

  const filteredLines = logs.split('\n').filter(l => !search || l.toLowerCase().includes(search.toLowerCase()));

  const copyLogs = () => {
    navigator.clipboard.writeText(logs);
    toast.success('Logs copiate');
  };

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 10 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
        <div style={{ position: 'relative', flex: 1, minWidth: 180 }}>
          <Search size={13} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
          <input className="form-input" style={{ paddingLeft: 28, fontSize: 12 }} placeholder="Filtrează logs..." value={search} onChange={e => setSearch(e.target.value)} />
        </div>
        <select className="form-select" style={{ width: 120 }} value={tail} onChange={e => setTail(parseInt(e.target.value))}>
          <option value={50}>50 linii</option>
          <option value={200}>200 linii</option>
          <option value={500}>500 linii</option>
          <option value={1000}>1000 linii</option>
        </select>
        <button className="btn btn-secondary" onClick={fetchLogs} title="Refresh"><RefreshCw size={13} /></button>
        <button className="btn btn-secondary" onClick={copyLogs} title="Copiază"><Copy size={13} /></button>
      </div>
      <div ref={logRef} style={{
        flex: 1, background: '#0d1117', borderRadius: 8, padding: 14, overflowY: 'auto',
        fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, color: '#c9d1d9',
        minHeight: 300, maxHeight: 500, border: '1px solid var(--border)',
      }}>
        {loading ? (
          <div style={{ textAlign: 'center', padding: 20, color: 'var(--text-secondary)' }}>
            <span className="spinner" style={{ borderColor: '#4f8ef7' }} />
          </div>
        ) : filteredLines.length === 0 ? (
          <div style={{ color: '#666', textAlign: 'center', padding: 20 }}>Nu există logs</div>
        ) : filteredLines.map((line, i) => {
          const isErr = /error|fail|fatal|exception/i.test(line);
          const isWarn = /warn|warning/i.test(line);
          return (
            <div key={i} style={{ color: isErr ? '#ff6b6b' : isWarn ? '#ffd93d' : '#c9d1d9', padding: '1px 0' }}>
              {line || '\u00a0'}
            </div>
          );
        })}
      </div>
      <div style={{ fontSize: 11, color: 'var(--text-secondary)' }}>
        {filteredLines.length} linii {search && `(filtrate din ${logs.split('\n').length})`}
      </div>
    </div>
  );
}

// ─── Terminal Panel ────────────────────────────────────────────────────────
function TerminalPanel({ containerId }) {
  const [history, setHistory] = useState([]);
  const [cmd, setCmd] = useState('');
  const [cmdHistory, setCmdHistory] = useState([]);
  const [histIdx, setHistIdx] = useState(-1);
  const [running, setRunning] = useState(false);
  const [workdir, setWorkdir] = useState('/');
  const termRef = useRef(null);
  const toast = useToast();

  const runCmd = async () => {
    if (!cmd.trim()) return;
    const currentCmd = cmd.trim();
    setCmd('');
    setCmdHistory(h => [currentCmd, ...h.slice(0, 49)]);
    setHistIdx(-1);
    setHistory(h => [...h, { type: 'cmd', text: `$ ${currentCmd}`, workdir }]);
    setRunning(true);
    try {
      const { data } = await api.post(`/docker/containers/${containerId}/exec`, { cmd: currentCmd, workdir });
      if (data.stdout) setHistory(h => [...h, { type: 'out', text: data.stdout }]);
      if (data.stderr) setHistory(h => [...h, { type: 'err', text: data.stderr }]);
      if (!data.stdout && !data.stderr) setHistory(h => [...h, { type: 'info', text: '(no output)' }]);
    } catch (e) {
      setHistory(h => [...h, { type: 'err', text: e.response?.data?.error || 'Eroare execuție' }]);
    } finally {
      setRunning(false);
      setTimeout(() => { if (termRef.current) termRef.current.scrollTop = termRef.current.scrollHeight; }, 50);
    }
  };

  const handleKey = (e) => {
    if (e.key === 'Enter') { runCmd(); }
    else if (e.key === 'ArrowUp') {
      e.preventDefault();
      const idx = histIdx + 1;
      if (idx < cmdHistory.length) { setHistIdx(idx); setCmd(cmdHistory[idx]); }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const idx = histIdx - 1;
      if (idx < 0) { setHistIdx(-1); setCmd(''); }
      else { setHistIdx(idx); setCmd(cmdHistory[idx]); }
    }
  };

  const clearTerminal = () => setHistory([]);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 8 }}>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <div style={{ fontSize: 12, color: 'var(--text-secondary)' }}>CWD:</div>
        <input className="form-input" style={{ width: 200, fontSize: 12 }} value={workdir} onChange={e => setWorkdir(e.target.value)} placeholder="/" />
        <button className="btn btn-secondary" style={{ fontSize: 11 }} onClick={clearTerminal}>Clear</button>
      </div>
      <div ref={termRef} style={{
        flex: 1, background: '#0d1117', borderRadius: 8, padding: 14,
        fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, color: '#c9d1d9',
        overflowY: 'auto', minHeight: 250, maxHeight: 450, border: '1px solid var(--border)',
      }}>
        {history.length === 0 && (
          <div style={{ color: '#666' }}>Terminal Docker — tastați comenzi și apăsați Enter</div>
        )}
        {history.map((h, i) => (
          <div key={i} style={{
            color: h.type === 'cmd' ? '#79c0ff' : h.type === 'err' ? '#ff6b6b' : h.type === 'info' ? '#666' : '#c9d1d9',
            whiteSpace: 'pre-wrap', wordBreak: 'break-all',
          }}>
            {h.type === 'cmd' && <span style={{ color: '#3fb950' }}>{h.workdir} </span>}
            {h.text}
          </div>
        ))}
        {running && <div style={{ color: '#666' }}>Rulează...</div>}
      </div>
      <div style={{ display: 'flex', gap: 8 }}>
        <span style={{ fontFamily: 'monospace', fontSize: 13, color: '#3fb950', alignSelf: 'center' }}>$</span>
        <input
          className="form-input"
          style={{ flex: 1, fontFamily: 'monospace', fontSize: 13, background: '#0d1117', color: '#c9d1d9', border: '1px solid var(--border)' }}
          value={cmd}
          onChange={e => setCmd(e.target.value)}
          onKeyDown={handleKey}
          placeholder="Enter command..."
          disabled={running}
          autoFocus
        />
        <button className="btn btn-primary" onClick={runCmd} disabled={running || !cmd.trim()}>
          {running ? <span className="spinner" /> : 'Run'}
        </button>
      </div>
    </div>
  );
}

// ─── Stats Panel ───────────────────────────────────────────────────────────
function StatsPanel({ containerId }) {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  const fetchStats = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/docker/containers/${containerId}/stats`);
      setStats(data);
    } catch (e) { toast.error(e.response?.data?.error || 'Container oprit sau eroare stats'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchStats(); }, []);

  if (loading) return <div className="loading-center"><span className="spinner" /></div>;
  if (!stats) return (
    <div style={{ textAlign: 'center', padding: 40 }}>
      <button className="btn btn-primary" onClick={fetchStats}><RefreshCw size={14} /> Încarcă Statistici</button>
      <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 8 }}>Containerul trebuie să fie activ</div>
    </div>
  );

  const statItems = [
    { label: 'CPU Usage', value: stats.cpu, icon: Cpu, color: 'var(--accent)' },
    { label: 'Memory Usage', value: stats.memory, icon: HardDrive, color: '#8b5cf6' },
    { label: 'Memory %', value: stats.memPerc, icon: Activity, color: 'var(--warning)' },
    { label: 'Network I/O', value: stats.netIO, icon: Network, color: 'var(--success)' },
    { label: 'Block I/O', value: stats.blockIO, icon: HardDrive, color: '#06b6d4' },
    { label: 'PIDs', value: stats.pids, icon: Settings, color: 'var(--text-secondary)' },
  ];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <button className="btn btn-secondary" onClick={fetchStats}><RefreshCw size={13} /> Actualizare</button>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12 }}>
        {statItems.map(s => (
          <div key={s.label} className="stat-card">
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
              <s.icon size={16} color={s.color} />
              <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{s.label}</span>
            </div>
            <div style={{ fontSize: 20, fontWeight: 800, color: s.color }}>{s.value || '—'}</div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─── File Manager ──────────────────────────────────────────────────────────
function FileManager({ containerId }) {
  const [currentPath, setCurrentPath] = useState('/');
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editFile, setEditFile] = useState(null);
  const [editContent, setEditContent] = useState('');
  const [saving, setSaving] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const [breadcrumbs, setBreadcrumbs] = useState(['/']);
  const fileInputRef = useRef(null);
  const toast = useToast();
  const { hasRole } = useAuth();

  const loadFiles = useCallback(async (dir) => {
    setLoading(true);
    try {
      const { data } = await api.get(`/docker/containers/${containerId}/files`, { params: { path: dir } });
      setFiles(data.files || []);
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare listare fișiere'); }
    finally { setLoading(false); }
  }, [containerId]);

  useEffect(() => { loadFiles(currentPath); }, [currentPath]);

  const navigate = (dir) => {
    setCurrentPath(dir);
    const parts = dir.split('/').filter(Boolean);
    setBreadcrumbs(['/', ...parts.map((p, i) => '/' + parts.slice(0, i + 1).join('/'))]);
  };

  const openFile = async (filePath) => {
    try {
      const { data } = await api.get(`/docker/containers/${containerId}/file`, { params: { path: filePath } });
      setEditFile(filePath);
      setEditContent(data.content || '');
    } catch (e) { toast.error('Nu se poate deschide fișierul'); }
  };

  const saveFile = async () => {
    setSaving(true);
    try {
      await api.put(`/docker/containers/${containerId}/file`, { filePath: editFile, content: editContent });
      toast.success('Fișier salvat');
      setEditFile(null);
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare salvare'); }
    finally { setSaving(false); }
  };

  const deleteFile = async (filePath) => {
    try {
      await api.delete(`/docker/containers/${containerId}/file`, { params: { path: filePath } });
      toast.success('Fișier șters');
      loadFiles(currentPath);
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare ștergere'); }
    setConfirm(null);
  };

  const downloadFile = (filePath) => {
    const token = localStorage.getItem('token');
    window.open(`/api/docker/containers/${containerId}/download?path=${encodeURIComponent(filePath)}&token=${token}`);
  };

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      await api.post(`/docker/containers/${containerId}/upload?path=${encodeURIComponent(currentPath)}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Fișier încărcat');
      loadFiles(currentPath);
    } catch (e) { toast.error(e.response?.data?.error || 'Eroare upload'); }
    e.target.value = '';
  };

  const getFileIcon = (f) => {
    if (f.isDir) return <Folder size={15} color="#f59e0b" />;
    const ext = f.name.split('.').pop().toLowerCase();
    const codeExts = ['js', 'ts', 'py', 'go', 'rs', 'java', 'php', 'rb', 'sh', 'yaml', 'yml', 'json', 'xml', 'html', 'css', 'conf', 'ini', 'toml', 'env'];
    if (codeExts.includes(ext)) return <FileText size={15} color="var(--accent)" />;
    return <File size={15} color="var(--text-secondary)" />;
  };

  const formatSize = (bytes) => {
    if (!bytes || bytes === 0) return '—';
    if (bytes < 1024) return `${bytes}B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)}KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)}MB`;
  };

  if (editFile) {
    const ext = editFile.split('.').pop().toLowerCase();
    return (
      <div style={{ display: 'flex', flexDirection: 'column', height: '100%', gap: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <button className="btn btn-secondary" onClick={() => setEditFile(null)}><X size={14} /> Înapoi</button>
          <span style={{ fontSize: 13, fontFamily: 'monospace', color: 'var(--text-secondary)', flex: 1 }}>{editFile}</span>
          <button className="btn btn-primary" onClick={saveFile} disabled={saving}>
            {saving ? <span className="spinner" /> : <><Save size={14} /> Salvare</>}
          </button>
        </div>
        <textarea
          style={{
            flex: 1, minHeight: 400, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6,
            background: '#0d1117', color: '#c9d1d9', border: '1px solid var(--border)',
            borderRadius: 8, padding: 14, resize: 'vertical', outline: 'none',
          }}
          value={editContent}
          onChange={e => setEditContent(e.target.value)}
          spellCheck={false}
        />
      </div>
    );
  }

  return (
    <div>
      {/* Breadcrumbs + actions */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, flex: 1, flexWrap: 'wrap' }}>
          {breadcrumbs.map((crumb, i) => (
            <React.Fragment key={crumb}>
              {i > 0 && <ChevronRight size={12} color="var(--text-secondary)" />}
              <button onClick={() => navigate(crumb)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: i === breadcrumbs.length - 1 ? 'var(--text-primary)' : 'var(--accent)', fontSize: 13, padding: '2px 4px', fontFamily: 'monospace' }}>
                {crumb === '/' ? '/' : crumb.split('/').pop()}
              </button>
            </React.Fragment>
          ))}
        </div>
        <input type="file" ref={fileInputRef} style={{ display: 'none' }} onChange={handleUpload} />
        <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => fileInputRef.current?.click()}>
          <Upload size={13} /> Upload
        </button>
        <button className="btn btn-secondary" style={{ fontSize: 12 }} onClick={() => loadFiles(currentPath)}>
          <RefreshCw size={13} />
        </button>
      </div>

      {loading ? (
        <div className="loading-center"><span className="spinner" /></div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr><th>NUME</th><th>PERMISIUNI</th><th>DIMENSIUNE</th><th>MODIFICAT</th><th>ACȚIUNI</th></tr>
            </thead>
            <tbody>
              {currentPath !== '/' && (
                <tr>
                  <td colSpan={5}>
                    <button onClick={() => {
                      const parent = currentPath.split('/').slice(0, -1).join('/') || '/';
                      navigate(parent);
                    }} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--accent)', display: 'flex', alignItems: 'center', gap: 6, fontSize: 13 }}>
                      <Folder size={14} color="var(--text-secondary)" /> ..
                    </button>
                  </td>
                </tr>
              )}
              {files.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: 'center', color: 'var(--text-secondary)', padding: 20 }}>Director gol</td></tr>
              )}
              {files.map(f => (
                <tr key={f.path}>
                  <td>
                    <button onClick={() => f.isDir ? navigate(f.path) : openFile(f.path)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: f.isDir ? '#f59e0b' : 'var(--text-primary)', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: f.isDir ? 600 : 400 }}>
                      {getFileIcon(f)} {f.name}
                    </button>
                  </td>
                  <td><span style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>{f.perms}</span></td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{formatSize(f.size)}</td>
                  <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{f.date}</td>
                  <td>
                    <div style={{ display: 'flex', gap: 4 }}>
                      {!f.isDir && (
                        <>
                          <button className="btn-icon" title="Editare" onClick={() => openFile(f.path)}><Edit3 size={13} /></button>
                          <button className="btn-icon" title="Descărcare" onClick={() => downloadFile(f.path)}><Download size={13} /></button>
                        </>
                      )}
                      {hasRole('superadmin', 'admin') && (
                        <button className="btn-icon" title="Ștergere" style={{ color: 'var(--danger)' }}
                          onClick={() => setConfirm({ msg: `Ștergi "${f.name}"?`, onConfirm: () => deleteFile(f.path) })}>
                          <Trash2 size={13} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {confirm && <ConfirmDialog msg={confirm.msg} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}

// ─── Container Details Modal ───────────────────────────────────────────────
function ContainerDetailModal({ containerId, onClose }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const toast = useToast();
  const { hasRole } = useAuth();

  useEffect(() => {
    api.get(`/docker/containers/${containerId}`)
      .then(({ data }) => setInfo(data))
      .catch(e => toast.error('Eroare la detalii container'))
      .finally(() => setLoading(false));
  }, [containerId]);

  const tabs = [
    { id: 'overview', label: 'Detalii', icon: Info },
    { id: 'logs', label: 'Logs', icon: FileText },
    { id: 'terminal', label: 'Terminal', icon: Terminal },
    { id: 'stats', label: 'Statistici', icon: BarChart2 },
    { id: 'files', label: 'Fișiere', icon: Folder },
  ];

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal" style={{ maxWidth: 900, width: '95vw', maxHeight: '90vh', display: 'flex', flexDirection: 'column' }}>
        <div className="modal-header">
          <span className="modal-title">
            <Container size={16} />
            {loading ? 'Container' : info?.name || containerId.substring(0, 12)}
            {info && <StatusBadge state={info.status} />}
          </span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: 2, padding: '0 20px', borderBottom: '1px solid var(--border)', overflowX: 'auto' }}>
          {tabs.map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 14px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === t.id ? 700 : 400, color: activeTab === t.id ? 'var(--accent)' : 'var(--text-secondary)', borderBottom: activeTab === t.id ? '2px solid var(--accent)' : '2px solid transparent', whiteSpace: 'nowrap' }}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        <div className="modal-body" style={{ flex: 1, overflowY: 'auto' }}>
          {loading ? (
            <div className="loading-center"><span className="spinner" /></div>
          ) : (
            <>
              {activeTab === 'overview' && info && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                  {[
                    { label: 'ID', value: info.id },
                    { label: 'Image', value: info.image },
                    { label: 'Status', value: <StatusBadge state={info.status} /> },
                    { label: 'IP Address', value: info.ipAddress || '—' },
                    { label: 'Hostname', value: info.hostname || '—' },
                    { label: 'WorkDir', value: info.workdir || '/' },
                    { label: 'PID', value: info.pid || '—' },
                    { label: 'Creat', value: info.created ? new Date(info.created).toLocaleString('ro-RO') : '—' },
                  ].map(item => (
                    <div key={item.label} style={{ background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 4, textTransform: 'uppercase', letterSpacing: 0.5 }}>{item.label}</div>
                      <div style={{ fontSize: 13, fontWeight: 600, fontFamily: typeof item.value === 'string' ? 'monospace' : 'inherit' }}>{item.value}</div>
                    </div>
                  ))}
                  {info.env?.length > 0 && (
                    <div style={{ gridColumn: '1/-1', background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Environment</div>
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                        {info.env.slice(0, 20).map((e, i) => (
                          <span key={i} style={{ fontFamily: 'monospace', fontSize: 11, background: 'var(--bg-primary)', padding: '3px 8px', borderRadius: 6, border: '1px solid var(--border)', color: 'var(--text-secondary)' }}>{e}</span>
                        ))}
                        {info.env.length > 20 && <span style={{ fontSize: 11, color: 'var(--text-secondary)' }}>+{info.env.length - 20} mai multe</span>}
                      </div>
                    </div>
                  )}
                  {info.mounts?.length > 0 && (
                    <div style={{ gridColumn: '1/-1', background: 'var(--bg-hover)', borderRadius: 8, padding: '10px 14px' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-secondary)', marginBottom: 8, textTransform: 'uppercase', letterSpacing: 0.5 }}>Mounts</div>
                      {info.mounts.map((m, i) => (
                        <div key={i} style={{ fontFamily: 'monospace', fontSize: 11, padding: '3px 0', color: 'var(--text-secondary)' }}>
                          {m.Source} → {m.Destination} ({m.Mode || 'rw'})
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {activeTab === 'logs' && <LogsPanel containerId={containerId} containerName={info?.name} />}
              {activeTab === 'terminal' && <TerminalPanel containerId={containerId} />}
              {activeTab === 'stats' && <StatsPanel containerId={containerId} />}
              {activeTab === 'files' && <FileManager containerId={containerId} />}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Docker Compose Modal ──────────────────────────────────────────────────
function ComposeModal({ onClose, onDone }) {
  const [content, setContent] = useState(`version: '3.8'
services:
  web:
    image: nginx:latest
    ports:
      - "8080:80"
    restart: unless-stopped
`);
  const [projectName, setProjectName] = useState('my-project');
  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState('');
  const toast = useToast();

  const run = async (action) => {
    setLoading(true);
    setOutput('');
    try {
      if (action === 'up') {
        const { data } = await api.post('/docker/compose/up', { composeContent: content, projectName });
        setOutput(data.output || 'Pornit cu succes');
        toast.success('Compose pornit');
      } else {
        const { data } = await api.post('/docker/compose/down', { projectName });
        setOutput(data.output || 'Oprit cu succes');
        toast.success('Compose oprit');
      }
      onDone();
    } catch (e) { setOutput(e.response?.data?.error || 'Eroare'); toast.error('Eroare compose'); }
    finally { setLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="modal modal-xl">
        <div className="modal-header">
          <span className="modal-title"><Layers size={16} /> Docker Compose</span>
          <button className="btn-icon" onClick={onClose}><X size={16} /></button>
        </div>
        <div className="modal-body">
          <div className="form-group">
            <label className="form-label">Nume Proiect</label>
            <input className="form-input" value={projectName} onChange={e => setProjectName(e.target.value)} placeholder="my-project" />
          </div>
          <div className="form-group">
            <label className="form-label">docker-compose.yml</label>
            <textarea style={{ width: '100%', minHeight: 300, fontFamily: 'monospace', fontSize: 12, lineHeight: 1.6, background: '#0d1117', color: '#c9d1d9', border: '1px solid var(--border)', borderRadius: 8, padding: 14, resize: 'vertical', outline: 'none', boxSizing: 'border-box' }}
              value={content} onChange={e => setContent(e.target.value)} spellCheck={false} />
          </div>
          {output && (
            <div style={{ background: '#0d1117', borderRadius: 8, padding: 14, fontFamily: 'monospace', fontSize: 12, color: '#c9d1d9', whiteSpace: 'pre-wrap', border: '1px solid var(--border)' }}>
              {output}
            </div>
          )}
        </div>
        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Închidere</button>
          <button className="btn" style={{ background: 'var(--danger)', color: '#fff' }} onClick={() => run('down')} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Square size={14} /> Down</>}
          </button>
          <button className="btn btn-primary" onClick={() => run('up')} disabled={loading}>
            {loading ? <span className="spinner" /> : <><Play size={14} /> Up -d</>}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Main Docker Page ──────────────────────────────────────────────────────
export default function Docker() {
  const [containers, setContainers] = useState([]);
  const [images, setImages] = useState([]);
  const [sysInfo, setSysInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAll, setShowAll] = useState(true);
  const [search, setSearch] = useState('');
  const [activeTab, setActiveTab] = useState('containers');
  const [selectedContainer, setSelectedContainer] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [showCompose, setShowCompose] = useState(false);
  const [confirm, setConfirm] = useState(null);
  const toast = useToast();
  const { hasRole } = useAuth();

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [cRes, iRes, sRes] = await Promise.all([
        api.get('/docker/containers', { params: { all: showAll } }),
        api.get('/docker/images').catch(() => ({ data: [] })),
        api.get('/docker/info').catch(() => ({ data: null })),
      ]);
      setContainers(cRes.data || []);
      setImages(iRes.data || []);
      setSysInfo(sRes.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Docker nu este disponibil. Verificați că Docker rulează pe server.');
    } finally { setLoading(false); }
  }, [showAll]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const action = async (id, act, name) => {
    try {
      await api.post(`/docker/containers/${id}/${act}`);
      toast.success(`Container ${act === 'start' ? 'pornit' : act === 'stop' ? 'oprit' : 'repornit'}`);
      loadAll();
    } catch (e) { toast.error(e.response?.data?.error || `Eroare ${act}`); }
  };

  const removeContainer = async (id, name) => {
    setConfirm({
      msg: `Ștergi containerul "${name}"? Această acțiune este ireversibilă.`,
      onConfirm: async () => {
        setConfirm(null);
        try {
          await api.delete(`/docker/containers/${id}`, { params: { force: true } });
          toast.success('Container șters');
          loadAll();
        } catch (e) { toast.error(e.response?.data?.error || 'Eroare ștergere'); }
      }
    });
  };

  const filtered = containers.filter(c =>
    !search || c.name?.toLowerCase().includes(search.toLowerCase()) ||
    c.image?.toLowerCase().includes(search.toLowerCase()) ||
    c.id?.includes(search)
  );

  const running = containers.filter(c => c.state === 'running').length;
  const stopped = containers.filter(c => c.state !== 'running').length;

  return (
    <div className="fade-in">
      {/* Header */}
      <div className="page-header">
        <div>
          <div className="page-title">Docker Manager</div>
          <div className="page-subtitle">Management containere, imagini și compose</div>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn btn-secondary" onClick={loadAll}><RefreshCw size={14} /></button>
          <button className="btn btn-secondary" onClick={() => setShowCompose(true)}><Layers size={14} /> Compose</button>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}><Plus size={14} /> Container Nou</button>
        </div>
      </div>

      {/* System Info KPIs */}
      {sysInfo && (
        <div className="stats-grid" style={{ marginBottom: 20 }}>
          {[
            { label: 'Containere Rulând', value: sysInfo.running ?? running, color: 'var(--success)', icon: Play },
            { label: 'Containere Oprite', value: sysInfo.stopped ?? stopped, color: 'var(--danger)', icon: Square },
            { label: 'Imagini', value: sysInfo.images, color: 'var(--accent)', icon: Package },
            { label: 'Versiune Docker', value: sysInfo.serverVersion || sysInfo.version || '—', color: '#8b5cf6', icon: Settings },
            { label: 'CPU', value: sysInfo.cpus ? `${sysInfo.cpus} cores` : '—', color: '#06b6d4', icon: Cpu },
            { label: 'OS', value: sysInfo.os || '—', color: 'var(--text-secondary)', icon: HardDrive },
          ].map(k => (
            <div key={k.label} className="stat-card">
              <div className="stat-icon" style={{ background: `${k.color}20`, marginBottom: 10 }}>
                <k.icon size={16} color={k.color} />
              </div>
              <div style={{ fontSize: 20, fontWeight: 800, color: k.color }}>{k.value}</div>
              <div style={{ fontSize: 12, color: 'var(--text-secondary)', marginTop: 3 }}>{k.label}</div>
            </div>
          ))}
        </div>
      )}

      {error && (
        <div style={{ display: 'flex', gap: 12, padding: 16, background: '#ef444420', border: '1px solid #ef4444', borderRadius: 10, marginBottom: 20, alignItems: 'center' }}>
          <AlertTriangle size={20} color="var(--danger)" />
          <div>
            <div style={{ fontWeight: 600, color: 'var(--danger)' }}>Docker indisponibil</div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{error}</div>
          </div>
          <button className="btn btn-secondary" style={{ marginLeft: 'auto' }} onClick={loadAll}><RefreshCw size={13} /></button>
        </div>
      )}

      {/* Tabs */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 2, borderBottom: '1px solid var(--border)', marginBottom: 16, overflowX: 'auto' }}>
          {[
            { id: 'containers', label: `Containere (${containers.length})`, icon: Container },
            { id: 'images', label: `Imagini (${images.length})`, icon: Package },
          ].map(t => (
            <button key={t.id} onClick={() => setActiveTab(t.id)}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '10px 16px', background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, fontWeight: activeTab === t.id ? 700 : 400, color: activeTab === t.id ? 'var(--accent)' : 'var(--text-secondary)', borderBottom: activeTab === t.id ? '2px solid var(--accent)' : '2px solid transparent', whiteSpace: 'nowrap' }}>
              <t.icon size={14} /> {t.label}
            </button>
          ))}
        </div>

        {activeTab === 'containers' && (
          <>
            {/* Filters */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 14, flexWrap: 'wrap' }}>
              <div style={{ position: 'relative', flex: 1, minWidth: 200 }}>
                <Search size={14} style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} />
                <input className="form-input" style={{ paddingLeft: 32 }} placeholder="Caută container, imagine, ID..." value={search} onChange={e => setSearch(e.target.value)} />
              </div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer', fontSize: 13, color: 'var(--text-secondary)' }}>
                <input type="checkbox" checked={showAll} onChange={e => setShowAll(e.target.checked)} />
                Afișează oprite
              </label>
            </div>

            {loading ? (
              <div className="loading-center"><span className="spinner" /></div>
            ) : filtered.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                <Container size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
                {search ? 'Niciun container găsit' : 'Niciun container. Creați unul cu butonul de mai sus.'}
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr>
                      <th>CONTAINER</th><th>IMAGE</th><th>STATUS</th><th>PORTURI</th><th>CREAT</th><th>ACȚIUNI</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map(c => (
                      <tr key={c.id}>
                        <td>
                          <div style={{ fontWeight: 600, fontSize: 14 }}>{c.name}</div>
                          <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>{c.id?.substring(0, 12)}</div>
                        </td>
                        <td style={{ fontFamily: 'monospace', fontSize: 12, color: 'var(--text-secondary)' }}>{c.image}</td>
                        <td><StatusBadge state={c.state} /></td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>{c.ports || '—'}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{c.created ? c.created.split(' ').slice(0, 2).join(' ') : '—'}</td>
                        <td>
                          <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
                            <button className="btn-icon" title="Detalii" onClick={() => setSelectedContainer(c.id)} style={{ color: 'var(--accent)' }}><Info size={14} /></button>
                            {c.state !== 'running' ? (
                              <button className="btn-icon" title="Pornire" style={{ color: 'var(--success)' }} onClick={() => action(c.id, 'start', c.name)}><Play size={14} /></button>
                            ) : (
                              <button className="btn-icon" title="Oprire" style={{ color: 'var(--warning)' }} onClick={() => action(c.id, 'stop', c.name)}><Square size={14} /></button>
                            )}
                            <button className="btn-icon" title="Repornire" onClick={() => action(c.id, 'restart', c.name)}><RotateCw size={14} /></button>
                            {hasRole('superadmin', 'admin') && (
                              <button className="btn-icon" title="Ștergere" style={{ color: 'var(--danger)' }} onClick={() => removeContainer(c.id, c.name)}><Trash2 size={14} /></button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </>
        )}

        {activeTab === 'images' && (
          loading ? <div className="loading-center"><span className="spinner" /></div> : (
            images.length === 0 ? (
              <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-secondary)' }}>
                <Package size={36} style={{ opacity: 0.3, display: 'block', margin: '0 auto 12px' }} />
                Nicio imagine Docker
              </div>
            ) : (
              <div className="table-wrapper">
                <table>
                  <thead>
                    <tr><th>REPOSITORY</th><th>TAG</th><th>IMAGE ID</th><th>CREAT</th><th>DIMENSIUNE</th></tr>
                  </thead>
                  <tbody>
                    {images.map((img, i) => (
                      <tr key={i}>
                        <td style={{ fontFamily: 'monospace', fontSize: 13 }}>{img.repo}</td>
                        <td><span className="badge badge-neutral" style={{ fontFamily: 'monospace' }}>{img.tag}</span></td>
                        <td style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text-secondary)' }}>{img.id}</td>
                        <td style={{ fontSize: 12, color: 'var(--text-secondary)' }}>{img.created}</td>
                        <td style={{ fontSize: 13, fontWeight: 600 }}>{img.size}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )
          )
        )}
      </div>

      {/* Modals */}
      {showCreate && <CreateContainerModal onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); loadAll(); }} />}
      {showCompose && <ComposeModal onClose={() => setShowCompose(false)} onDone={loadAll} />}
      {selectedContainer && <ContainerDetailModal containerId={selectedContainer} onClose={() => setSelectedContainer(null)} />}
      {confirm && <ConfirmDialog msg={confirm.msg} onConfirm={confirm.onConfirm} onCancel={() => setConfirm(null)} />}
    </div>
  );
}
